import React, { useId } from 'react';
import cx from 'classnames';

import FieldLabel from './FieldLabel';

type Props = {
  label: string;
  required?: boolean;
  optionalHint?: string;
  placeholder?: string;
  icon?: React.ReactNode;
  trailing?: React.ReactNode;
  type?: 'text' | 'email' | 'password';
  autoComplete?: string;
  autoFocus?: boolean;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  name?: string;
};

function TextInput({
  label,
  required = false,
  optionalHint,
  placeholder,
  icon,
  trailing,
  type = 'text',
  autoComplete,
  autoFocus = false,
  value,
  onChange,
  error,
  name,
}: Props) {
  const generatedId = useId();
  const inputId = name || generatedId;
  const errorId = `${inputId}-error`;

  return (
    <div className="w-full font-lexend">
      <FieldLabel htmlFor={inputId} required={required} optionalHint={optionalHint}>
        {label}
      </FieldLabel>
      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-retino-grey">
            {icon}
          </span>
        )}
        <input
          id={inputId}
          name={name}
          type={type}
          autoComplete={autoComplete}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          value={value}
          placeholder={placeholder}
          required={required}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className={cx(
            'h-12 w-full rounded-xl border bg-white px-3 text-sm text-black outline-none transition placeholder:text-retino-grey/70 focus:border-retino-darkviolet focus:ring-2 focus:ring-retino-lightviolet max-sm:text-base',
            {
              'pl-11': icon,
              'pr-12': trailing,
              'border-red-500': error,
              'border-slate-300': !error,
            },
          )}
        />
        {trailing && <span className="absolute inset-y-0 right-1 flex items-center">{trailing}</span>}
      </div>
      {error && (
        <p id={errorId} className="mt-2 text-[13px] leading-snug text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

export default TextInput;
